import { FunctionComponent } from 'react';
import { formatPhoneNumber } from '@spike/phone-utils';
import { Table } from '../../UI/V3/Table/Table';
import { TableHeader } from '../../UI/V3/Table/Header/TableHeader';
import { TableHeaderCell } from '../../UI/V3/Table/HeaderCell/TableHeaderCell';
import { TableBody } from '../../UI/V3/Table/Body/TableBody';
import { TableRow } from '../../UI/V3/Table/Row/TableRow';
import { TableCell } from '../../UI/V3/Table/Cell/TableCell';
import DefaultTableEmptyState from '../../UI/V3/Table/EmptyState/DefaultTableEmptyState';
import { PaginationProps } from '../../UI/V3/Pagination/Pagination';
import { Spinner } from '../../UI/V3/Spinner/Spinner';
import Button from '../../UI/V3/Button/Button';
import { LocationStaffMember, LocationStaffSchedule } from '../../../model/location';
import styles from './LocationDetail.module.scss';

const COLUMN_COUNT = 7;

const scheduleLabels: Record<LocationStaffSchedule, string> = {
    default: 'Default',
    custom: 'Custom'
};

interface LocationStaffTableProps {
    staff: Array<LocationStaffMember>;
    pagination: PaginationProps;
    loading: boolean;
    error?: string;
    onPageChange: (page: number) => void;
    onSizeChange: (size: number) => void;
    onLogin: (member: LocationStaffMember) => void;
}

export const LocationStaffTable: FunctionComponent<LocationStaffTableProps> = ({
    staff,
    pagination,
    loading,
    error,
    onPageChange,
    onSizeChange,
    onLogin
}) => {
    const renderBody = () => {
        if (loading) {
            return (
                <TableBody>
                    <tr>
                        <td
                            colSpan={COLUMN_COUNT}
                            className={styles.spinnerCell}
                        >
                            <Spinner />
                        </td>
                    </tr>
                </TableBody>
            );
        }

        if (error) {
            return (
                <TableBody className={styles.tableBodyEmpty}>
                    <DefaultTableEmptyState
                        title="Unable to load staff"
                        description={[error]}
                    />
                </TableBody>
            );
        }

        if (staff.length === 0) {
            return (
                <TableBody className={styles.tableBodyEmpty}>
                    <DefaultTableEmptyState
                        title="No staff found"
                        description={['No staff members match your current search criteria.']}
                    />
                </TableBody>
            );
        }

        return (
            <TableBody>
                {staff.map(member => (
                    <TableRow
                        key={member.id}
                        className={styles.staffRow}
                    >
                        <TableCell>{member.name}</TableCell>
                        <TableCell>{member.role}</TableCell>
                        <TableCell>{scheduleLabels[member.schedule] ?? member.schedule}</TableCell>
                        <TableCell>{`${member.servicesCount} Services`}</TableCell>
                        <TableCell>{member.email}</TableCell>
                        <TableCell>{formatPhoneNumber(member.phone, 'US') || 'N/A'}</TableCell>
                        <TableCell isActions>
                            <Button
                                label="Login"
                                variant="black"
                                size="small"
                                disabled={!member.active}
                                onClick={() => onLogin(member)}
                            />
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        );
    };

    return (
        <Table
            pagination={{ ...pagination, isLoading: loading }}
            onPageChange={onPageChange}
            onSizeChange={onSizeChange}
            className={styles.table}
            fullHeight={true}
        >
            <TableHeader className={styles.tableHeader}>
                <TableHeaderCell>Name</TableHeaderCell>
                <TableHeaderCell>Role</TableHeaderCell>
                <TableHeaderCell>Schedule</TableHeaderCell>
                <TableHeaderCell>Services</TableHeaderCell>
                <TableHeaderCell>Email</TableHeaderCell>
                <TableHeaderCell>Phone</TableHeaderCell>
                <TableHeaderCell></TableHeaderCell>
            </TableHeader>

            {renderBody()}
        </Table>
    );
};

export default LocationStaffTable;
